<script>
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import FilterBar from '../components/FilterBar.svelte';
  import MethodChip from '../components/MethodChip.svelte';
  import CaptureDetail from '../components/CaptureDetail.svelte';
  import CaptureHelp from '../components/CaptureHelp.svelte';
  import { capturePayload } from '../lib/capture.js';
  import { ui } from '../lib/ui.svelte.js';

  // Server-configured retention window. Captures TTL out after this many
  // days, so the date picker treats it as the hard floor. The *default*
  // `since` is 24h ago -- most webhook debugging looks at recent events,
  // and asking mongo for a 30-day scan on every page load is wasteful.
  // /captures/_meta is fetched on mount to learn the real retention value.
  let retentionDays = $state(30);
  const DEFAULT_LOOKBACK_HOURS = 24;

  function toLocalIso(d) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  function defaultSince() {
    return toLocalIso(new Date(Date.now() - DEFAULT_LOOKBACK_HOURS * 60 * 60 * 1000));
  }
  function defaultUntil() { return toLocalIso(new Date()); }
  function floorIso() {
    return toLocalIso(new Date(Date.now() - retentionDays * 24 * 60 * 60 * 1000));
  }

  // URL <-> state: the same set of filter keys round-trips through the
  // address bar, so a Captures URL is shareable with a teammate and a
  // re-login (via rememberReturnTo) lands you on the same view you left.
  const FILTER_KEYS = ['bucket_id', 'method', 'path', 'subject', 'header_key', 'header_value', 'since', 'until'];

  // The date inputs work in the browser's local time (datetime-local has no
  // zone). The URL instead carries an absolute unix timestamp (seconds) so a
  // shared link resolves to the same instant in any timezone -- a local
  // "2026-06-04T19:07" string in the URL meant different moments for the
  // sender and the recipient, which is what made captures look "missing".
  function localToUnix(local) {
    if (!local) return '';
    const ms = new Date(local).getTime();
    return Number.isNaN(ms) ? '' : String(Math.floor(ms / 1000));
  }
  function unixToLocal(v) {
    if (v == null || v === '') return '';
    // Back-compat: pre-unix links carried a datetime-local string already.
    return /^\d+$/.test(String(v)) ? toLocalIso(new Date(Number(v) * 1000)) : String(v);
  }

  function readUrlSeed() {
    if (typeof window === 'undefined') return {};
    const u = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of FILTER_KEYS) {
      const v = u.get(k);
      if (v == null) continue;
      out[k] = (k === 'since' || k === 'until') ? unixToLocal(v) : v;
    }
    return out;
  }

  const seeded = readUrlSeed();
  let filter = $state({
    bucket_id:     seeded.bucket_id     ?? '',
    method:        seeded.method        ?? '',
    path:          seeded.path          ?? '',
    subject:       seeded.subject       ?? '',
    header_key:    seeded.header_key    ?? '',
    header_value:  seeded.header_value  ?? '',
    since:         seeded.since         ?? defaultSince(),
    // Leave `until` open by default so the view stays live: a capture sent
    // after page-load still appears on reload/refresh. Freezing it to "now"
    // here baked a stale upper bound into the shareable URL, so anything that
    // arrived after that minute fell outside the window and looked "missing".
    // An explicit `until` from the URL is still honored; `defaultUntil()`
    // remains the picker's max so the future stays unselectable.
    until:         seeded.until         ?? '',
  });
  let captures = $state([]);
  let total = $state(0);
  let loading = $state(false);
  let err = $state('');
  let skip = $state(0);
  let limit = $state(40);
  let sentinel = $state(null);
  let listCtl = null;
  let downloading = $state(false);

  // Collapsible filter card (mirrors the Logs page). Starts open; the active
  // text-filter count stays visible on the header while collapsed so you can
  // tell at a glance whether a filter is hidden.
  let filtersOpen = $state(true);
  const activeFilters = $derived(
    [filter.bucket_id, filter.method, filter.path, filter.subject, filter.header_key, filter.header_value]
      .filter(Boolean).length
  );

  // Expand + selection state, keyed by capture id (mirrors the Logs table).
  let expanded = $state(new Set()); // ids currently expanded
  let selected = $state(new Set()); // ids currently checked
  let lastSelIndex = -1;            // anchor row for shift-click range select
  let listScroll = $state(null);    // bounded scroller -> infinite-scroll root

  function isAbort(e) {
    return e?.name === 'AbortError' || /aborted|signal is aborted/i.test(String(e?.message || ''));
  }

  // Clamp the date range to [retention floor, now] before sending. The
  // picker UI already disables out-of-range cells, but a user could paste
  // an old URL or type a literal value into the input -- guard server-side
  // intent by clamping client-side too.
  function clampedRange() {
    const floor = new Date(Date.now() - retentionDays * 24 * 60 * 60 * 1000);
    const ceil  = new Date();
    let since = filter.since ? new Date(filter.since) : null;
    let until = filter.until ? new Date(filter.until) : null;
    if (since && since < floor) since = floor;
    if (until && until > ceil)  until = ceil;
    return {
      since: since ? since.toISOString() : '',
      until: until ? until.toISOString() : '',
    };
  }

  // Push the current filter state into the URL so refreshing the tab,
  // copy-pasting the URL or being bounced through login (which preserves
  // pathname+search via rememberReturnTo) lands the user back where they
  // were. Uses replaceState so the back button still walks app history.
  // since/until are written as absolute unix seconds (see localToUnix).
  function syncUrl() {
    if (typeof window === 'undefined') return;
    const u = new URLSearchParams();
    for (const k of FILTER_KEYS) {
      const v = (k === 'since' || k === 'until') ? localToUnix(filter[k]) : filter[k];
      if (v != null && v !== '') u.set(k, String(v));
    }
    const qs = u.toString();
    history.replaceState(null, '', '/captures' + (qs ? '?' + qs : ''));
  }

  async function load(resetPage = true) {
    listCtl?.abort();
    listCtl = new AbortController();
    loading = true;
    err = '';
    try {
      if (resetPage) {
        skip = 0;
        // Selection + expansion track ids that may not exist in the new
        // result set; clear them so the toolbar count stays honest.
        if (selected.size) selected = new Set();
        if (expanded.size) expanded = new Set();
        lastSelIndex = -1;
      }
      const range = clampedRange();
      const params = { ...filter, ...range, skip, limit };
      const r = await api.listCaptures(params, { signal: listCtl.signal });
      const page = r.captures || [];
      captures = resetPage ? page : captures.concat(page);
      total = r.total || 0;
      syncUrl();
    } catch (e) {
      if (isAbort(e)) return;
      err = e.message;
    } finally {
      loading = false;
    }
  }

  async function loadMore() {
    skip += limit;
    await load(false);
    if (sentinel && captures.length < total) {
      const r = sentinel.getBoundingClientRect();
      if (r.top < window.innerHeight) queueMicrotask(loadMore);
    }
  }

  // Download the full filtered set (not just the page on screen) as JSON.
  async function download() {
    if (downloading) return;
    downloading = true;
    try {
      const range = clampedRange();
      const all = [];
      const PAGE = 200, MAX = 50_000;
      for (let s = 0; s < MAX; s += PAGE) {
        const r = await api.listCaptures({ ...filter, ...range, skip: s, limit: PAGE });
        const rows = r.captures || [];
        all.push(...rows);
        if (rows.length < PAGE) break;
      }
      saveJson(all, `captures-${stamp()}.json`);
      ui.snack(`Downloaded ${all.length.toLocaleString()} capture${all.length === 1 ? '' : 's'}`, 'success');
    } catch (e) {
      ui.snack('Download failed: ' + e.message, 'error');
    } finally {
      downloading = false;
    }
  }

  onMount(async () => {
    try {
      const m = await api.captureMeta();
      if (m?.retention_days > 0) retentionDays = m.retention_days;
    } catch (_) { /* fall back to 30; UI still renders */ }
    load();
  });

  // Infinite scroll: observe the sentinel against the bounded list container
  // so intersection is measured inside the table's own scroll area.
  $effect(() => {
    if (!sentinel) return;
    const obs = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      if (loading) return;
      if (captures.length >= total) return;
      loadMore();
    }, { root: listScroll || null, rootMargin: '200px 0px' });
    obs.observe(sentinel);
    return () => obs.disconnect();
  });

  // --- Expand + selection ----------------------------------------------
  function toggle(id) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    expanded = next;
  }
  // Click toggles one; shift-click selects the range from the last-clicked
  // row to this one. onclick (not onchange) so we capture shiftKey.
  function toggleSelect(index, shiftKey) {
    const next = new Set(selected);
    if (shiftKey && lastSelIndex >= 0) {
      const lo = Math.min(lastSelIndex, index), hi = Math.max(lastSelIndex, index);
      for (let i = lo; i <= hi; i++) next.add(captures[i].id);
    } else {
      const id = captures[index].id;
      if (next.has(id)) next.delete(id); else next.add(id);
    }
    selected = next;
    lastSelIndex = index;
  }
  let allOnPageSelected = $derived(captures.length > 0 && captures.every((c) => selected.has(c.id)));
  function selectAllOnPage() {
    selected = allOnPageSelected ? new Set() : new Set(captures.map((c) => c.id));
  }
  function clearSelection() { selected = new Set(); lastSelIndex = -1; }

  // --- Bulk actions on the current selection ---------------------------
  function stamp() { return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); }
  function saveJson(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  function selectedPayloads() {
    return captures.filter((c) => selected.has(c.id)).map(capturePayload);
  }

  async function copyOne(c) {
    try {
      await navigator.clipboard.writeText(JSON.stringify(capturePayload(c), null, 2));
      ui.snack('Capture copied', 'success', 1500);
    } catch (e) { ui.snack('Copy failed: ' + e.message, 'error'); }
  }
  async function copySelected() {
    const docs = selectedPayloads();
    if (!docs.length) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(docs, null, 2));
      ui.snack(`Copied ${docs.length.toLocaleString()} capture${docs.length === 1 ? '' : 's'}`, 'success', 1500);
    } catch (e) { ui.snack('Copy failed: ' + e.message, 'error'); }
  }
  function downloadSelected() {
    const docs = selectedPayloads();
    if (!docs.length) return;
    saveJson(docs, `captures-selected-${stamp()}.json`);
    ui.snack(`Downloaded ${docs.length.toLocaleString()} capture${docs.length === 1 ? '' : 's'}`, 'success');
  }
  async function deleteSelected() {
    const ids = [...selected];
    if (!ids.length) return;
    const ok = await ui.ask({
      title: `Delete ${ids.length} ${ids.length === 1 ? 'capture' : 'captures'}?`,
      message: 'The selected captures will be permanently removed.',
      confirmText: 'Delete', destructive: true,
    });
    if (!ok) return;
    try {
      const r = await api.deleteCapturesByIds(ids);
      const gone = new Set(ids);
      captures = captures.filter((c) => !gone.has(c.id));
      total = Math.max(0, total - (r?.deleted ?? ids.length));
      clearSelection();
      ui.snack(`Deleted ${r?.deleted ?? ids.length}`, 'success');
    } catch (e) { ui.snack(e.message, 'error'); }
  }

  function fmtTime(t) { return t ? new Date(t).toLocaleString() : '—'; }
</script>

<div class="cap-page">
<CaptureHelp bucketId={filter.bucket_id} />

<div class="shrink-0">
  <button type="button" class="cap-filter-toggle" onclick={() => (filtersOpen = !filtersOpen)} aria-expanded={filtersOpen}>
    <span class="material-symbols-outlined cap-filter-chevron" style:transform={filtersOpen ? 'rotate(90deg)' : ''}>chevron_right</span>
    <span class="font-medium">Filters</span>
    {#if activeFilters > 0}<span class="cap-filter-count">{activeFilters}</span>{/if}
  </button>

  {#if filtersOpen}
    <FilterBar bind:filter onApply={() => load()} dateMin={floorIso()} dateMax={defaultUntil()} />

    <p class="text-xs text-on-surface-variant -mt-2 mb-3">
      Default window is the last {DEFAULT_LOOKBACK_HOURS} hours. Captures are kept for {retentionDays} days — the picker won't let you go earlier.
    </p>
  {/if}

  <!-- List toolbar: total + actions, sitting directly above the list. -->
  <div class="flex items-center justify-between mb-3 gap-3 flex-wrap">
    <span class="text-sm text-on-surface-variant flex items-center gap-2">
      {#if loading || downloading}
        <span class="material-symbols-outlined text-primary cap-spin" style="font-size: 18px;" aria-hidden="true" title="Loading">progress_activity</span>
      {/if}
      {total.toLocaleString()} total
    </span>
    <div class="flex items-center gap-2">
      <button
        onclick={() => load()}
        disabled={loading}
        class="px-4 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
        title="Reload the current filter"
      >
        <span class="material-symbols-outlined text-base align-middle">refresh</span>
        Refresh
      </button>
      <button
        onclick={download}
        disabled={downloading || captures.length === 0}
        class="px-4 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
        title="Download every capture that matches the current filter as JSON"
      >
        <span class="material-symbols-outlined text-base align-middle">download</span>
        {downloading ? 'Downloading…' : 'Download'}
      </button>
    </div>
  </div>
</div>

{#if err}
  <p class="text-error">{err}</p>
{:else if loading && captures.length === 0}
  <p class="text-on-surface-variant">Loading…</p>
{:else if captures.length === 0}
  <div class="bg-surface-container rounded-2xl p-12 text-center text-on-surface-variant">
    <span class="material-symbols-outlined text-5xl mb-2">inbox</span>
    <p>No captures match these filters yet.</p>
    <p class="text-sm mt-2">Send a request to <code class="bg-surface-container-high px-2 py-0.5 rounded">/h/&lt;anything&gt;</code> to see it here.</p>
  </div>
{:else}
  <div class="cap-list-scroll" bind:this={listScroll}>
    <div class="cap-table">
      <!-- Header row. Subgrid keeps these cells aligned with every data row. -->
      <div class="cap-thead">
        <span class="cap-th cap-th-check">
          <label class="cap-checkbox-wrap" title="Select all on this page">
            <input type="checkbox" checked={allOnPageSelected} onchange={selectAllOnPage} class="cap-checkbox" aria-label="Select all on page" />
          </label>
        </span>
        <span class="cap-th" aria-hidden="true"></span>
        <span class="cap-th">Method</span>
        <span class="cap-th">Path</span>
        <span class="cap-th">Subject</span>
        <span class="cap-th">User-Agent</span>
        <span class="cap-th">Bucket</span>
        <span class="cap-th">Size</span>
        <span class="cap-th">Remote IP</span>
        <span class="cap-th">Received</span>
      </div>

      {#each captures as c, i (c.id)}
        {@const open = expanded.has(c.id)}
        {@const sel = selected.has(c.id)}
        <div class="cap-trow" class:cap-row-selected={sel}>
          <label class="cap-td cap-td-check">
            <input type="checkbox" checked={sel} onclick={(e) => toggleSelect(i, e.shiftKey)} class="cap-checkbox" aria-label="Select capture" />
          </label>
          <button type="button" class="cap-td cap-td-chevron" onclick={() => toggle(c.id)} aria-expanded={open} aria-label={open ? 'Collapse capture' : 'Expand capture'}>
            <span class="material-symbols-outlined cap-chevron" style:transform={open ? 'rotate(90deg)' : ''}>chevron_right</span>
          </button>
          <div class="cap-td"><MethodChip method={c.method} /></div>
          <div class="cap-td cap-cell-path" title={(c.path || '/') + (c.query ? '?' + c.query : '')}>
            <a href={`/captures/${c.id}`} class="cap-path-link">{c.path || '/'}</a>{#if c.query}<span class="cap-path-query">?{c.query}</span>{/if}
          </div>
          <div class="cap-td cap-cell-mono" title={c.subject || ''}>{c.subject || '—'}</div>
          <div class="cap-td" title={c.user_agent || ''}>{c.user_agent || '—'}</div>
          <div class="cap-td cap-cell-mono" title={c.bucket_id || 'global'}>{c.bucket_id || 'global'}</div>
          <div class="cap-td cap-cell-nowrap">{c.body_size}b{c.body_truncated ? '*' : ''}</div>
          <div class="cap-td cap-cell-mono" title={c.remote_addr || ''}>{c.remote_addr || '—'}</div>
          <div class="cap-td cap-cell-nowrap">{fmtTime(c.created_at)}</div>
        </div>
        {#if open}
          <div class="cap-detailrow">
            <button type="button" class="cap-detail-copy" onclick={() => copyOne(c)} title="Copy capture JSON" aria-label="Copy capture JSON">
              <span class="material-symbols-outlined text-base">content_copy</span>
            </button>
            <CaptureDetail capture={c} />
          </div>
        {/if}
      {/each}
    </div>

    <div class="flex flex-col items-center mt-4 gap-2">
      {#if captures.length < total}
        <div bind:this={sentinel} class="cap-sentinel" aria-hidden="true"></div>
        <button
          onclick={loadMore}
          disabled={loading}
          class="px-6 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
        >
          {loading ? 'Loading…' : `Load ${limit} more`}
        </button>
      {:else}
        <span class="text-xs text-on-surface-variant">All {captures.length.toLocaleString()} matching captures shown.</span>
      {/if}
    </div>
  </div>

  <!-- Floating selection toolbar. Acts on the in-memory rows (what's actually
       loaded), matching the Logs table. Copy / Download are client-side;
       Delete hits the bulk delete-by-ids endpoint. -->
  {#if selected.size > 0}
    <div class="cap-selbar" role="toolbar" aria-label="Selection actions">
      <span class="cap-selbar-count">{selected.size.toLocaleString()} selected</span>
      <button onclick={copySelected} class="cap-selbar-btn">
        <span class="material-symbols-outlined text-base">content_copy</span>
        Copy
      </button>
      <button onclick={downloadSelected} class="cap-selbar-btn">
        <span class="material-symbols-outlined text-base">download</span>
        Download
      </button>
      <button onclick={deleteSelected} class="cap-selbar-btn cap-selbar-btn-danger">
        <span class="material-symbols-outlined text-base">delete</span>
        Delete
      </button>
      <button onclick={clearSelection} class="cap-selbar-btn-text" title="Clear selection">
        <span class="material-symbols-outlined text-base">close</span>
      </button>
    </div>
  {/if}
{/if}
</div>

<style>
  /* Fill the content pane and lay out as a column: help + filter + toolbar take
     their natural height, the table flexes to fill the rest and is the only
     scroller (no page-scroller + list-scroller double scrollbar). */
  .cap-page {
    block-size: 100%;
    display: flex;
    flex-direction: column;
    min-block-size: 0;
  }

  .cap-spin { animation: cap-spin 1s linear infinite; }
  @keyframes cap-spin { to { transform: rotate(360deg); } }

  /* Collapsible-filters header (matches the Logs "Filters" toggle). */
  .cap-filter-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    inline-size: 100%;
    padding: 0.5rem 0.25rem;
    margin-block-end: 0.5rem;
    background: transparent;
    border: 0;
    color: rgb(var(--md-sys-color-on-surface));
    cursor: pointer;
    text-align: start;
  }
  .cap-filter-chevron { font-size: 20px; color: rgb(var(--md-sys-color-on-surface-variant)); transition: transform 150ms; }
  .cap-filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-inline-size: 20px;
    block-size: 20px;
    padding-inline: 6px;
    border-radius: 999px;
    background: rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-on-primary));
    font-size: 12px;
    font-weight: 600;
  }

  /* The table is the single scroller: flexes to fill the height left under the
     filter + toolbar, and scrolls on both axes (wide table scrolls-x on narrow
     viewports instead of crushing columns). The rounded surface + clipping live
     here so the sticky header works. */
  .cap-list-scroll {
    flex: 1;
    min-block-size: 0;
    overflow: auto;
    background: rgb(var(--md-sys-color-surface-container));
    border-radius: 16px;
  }

  /* Grid "table": one track per field. Header + every row use
     `grid-template-columns: subgrid` so cells align to these tracks. */
  .cap-table {
    display: grid;
    grid-template-columns:
      40px                     /* select */
      36px                     /* expand */
      84px                     /* Method */
      minmax(180px, 2fr)       /* Path */
      minmax(110px, 1fr)       /* Subject */
      minmax(150px, 1.4fr)     /* User-Agent */
      minmax(110px, 0.9fr)     /* Bucket */
      72px                     /* Size */
      minmax(120px, 1fr)       /* Remote IP */
      minmax(140px, 1fr);      /* Received */
    align-content: start;
    min-inline-size: 1040px;
  }
  .cap-thead,
  .cap-trow {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    align-items: center;
  }
  .cap-thead {
    position: sticky;
    inset-block-start: 0;
    z-index: 2;
    background: rgb(var(--md-sys-color-surface-container-high));
    border-block-end: 1px solid rgb(var(--md-sys-color-outline-variant));
  }
  .cap-th {
    padding: 0.625rem 0.75rem;
    font-size: 12px;
    font-weight: 600;
    color: rgb(var(--md-sys-color-on-surface-variant));
    white-space: nowrap;
  }
  .cap-trow { border-block-start: 1px solid rgb(var(--md-sys-color-outline-variant) / 0.5); }
  .cap-trow:hover { background: rgb(var(--md-sys-color-on-surface) / 0.04); }
  .cap-row-selected { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .cap-td {
    min-inline-size: 0;
    padding: 0.5rem 0.75rem;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cap-cell-mono { font-family: 'Menlo','Consolas',monospace; font-size: 12px; }
  .cap-cell-nowrap { white-space: nowrap; }
  .cap-cell-path { font-family: 'Menlo','Consolas',monospace; font-size: 12px; }
  .cap-path-link { color: inherit; text-decoration: none; }
  .cap-path-link:hover { color: rgb(var(--md-sys-color-primary)); text-decoration: underline; }
  .cap-path-query { color: rgb(var(--md-sys-color-on-surface-variant)); }
  .cap-td-check { display: flex; align-items: center; justify-content: center; padding-inline: 0; }
  .cap-td-chevron {
    display: flex; align-items: center; justify-content: center;
    padding: 0; border: 0; background: transparent; cursor: pointer;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .cap-td-chevron:hover { color: rgb(var(--md-sys-color-primary)); }
  .cap-chevron { font-size: 20px; transition: transform 150ms; }

  /* Expanded detail spans the full table width below its row. */
  .cap-detailrow {
    grid-column: 1 / -1;
    position: relative;
    border-block-start: 1px solid rgb(var(--md-sys-color-outline-variant));
    background: rgb(var(--md-sys-color-surface));
  }
  .cap-detail-copy {
    position: absolute; inset-block-start: 6px; inset-inline-end: 10px;
    display: inline-flex; align-items: center; justify-content: center;
    inline-size: 30px; block-size: 30px; border-radius: 8px; z-index: 1;
    border: 0; cursor: pointer;
    background: rgb(var(--md-sys-color-surface-container));
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .cap-detail-copy:hover { color: rgb(var(--md-sys-color-primary)); }

  .cap-checkbox-wrap { display: inline-flex; align-items: center; cursor: pointer; }
  .cap-checkbox {
    inline-size: 16px;
    block-size: 16px;
    accent-color: rgb(var(--md-sys-color-primary));
    cursor: pointer;
  }

  .cap-sentinel { inline-size: 100%; block-size: 1px; }

  /* Floating selection toolbar pinned to the bottom-center of the viewport.
     Filled inverse surface, full-pill buttons, soft shadow (M3). */
  .cap-selbar {
    position: fixed;
    inset-block-end: 1.5rem;
    inset-inline: 0;
    margin-inline: auto;
    inline-size: max-content;
    max-inline-size: calc(100vw - 2rem);
    z-index: 30;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    background: rgb(var(--md-sys-color-inverse-surface));
    color: rgb(var(--md-sys-color-inverse-on-surface));
    border-radius: 999px;
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.18);
  }
  .cap-selbar-count { font-size: 13px; font-weight: 500; padding-inline: 0.5rem; }
  .cap-selbar-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.875rem;
    border-radius: 999px;
    background: transparent;
    color: inherit;
    border: 0;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
  }
  .cap-selbar-btn:hover { background: rgb(var(--md-sys-color-inverse-on-surface) / 0.12); }
  .cap-selbar-btn-danger:hover { background: rgb(var(--md-sys-color-error) / 0.22); color: rgb(var(--md-sys-color-error)); }
  .cap-selbar-btn-text {
    display: inline-flex;
    align-items: center;
    inline-size: 32px;
    block-size: 32px;
    justify-content: center;
    border-radius: 999px;
    background: transparent;
    color: inherit;
    border: 0;
    cursor: pointer;
  }
  .cap-selbar-btn-text:hover { background: rgb(var(--md-sys-color-inverse-on-surface) / 0.12); }

  /* Lift above AppShell's fixed mobile bottom-nav on small screens. */
  @media (max-width: 768px) {
    .cap-selbar { inset-block-end: 5rem; }
  }
</style>
